import { useState, useEffect, useMemo, useRef } from "react";
import { FormModal } from "@/components/form/FormModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { useParties } from "@/contexts/PartyContext";
import { useCompany } from "@/contexts/CompanyContext";
import { Party, PartyType, EntityType, CreatePartyInput } from "@/types/party";
import {
  INDIAN_STATES_WITH_GST_CODES,
  PAN_ENTITY_TYPES,
  BUSINESS_CONSTITUTIONS,
  validatePAN,
  validateGSTIN,
  getStateByCode,
  getStateByName,
} from "@/lib/onboarding/gstUtils";
import {
  Building2, User, Phone, Mail, MapPin, ShieldCheck, CheckCircle2,
  ChevronDown, ChevronUp, Plus, RefreshCw, AlertTriangle, Info
} from "lucide-react";

interface QuickPartyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partyType?: "customer" | "supplier" | "both";
  initialName?: string;
  onPartyCreated?: (party: Party, ledgerId: string) => void;
}

export function QuickPartyModal({
  open,
  onOpenChange,
  partyType = "customer",
  initialName = "",
  onPartyCreated,
}: QuickPartyModalProps) {
  const { toast } = useToast();
  const { createParty } = useParties();
  const { activeCompany } = useCompany();

  const defaultState = activeCompany?.state || "Maharashtra";
  const defaultStateObj = getStateByName(defaultState);
  const defaultStateCode = defaultStateObj?.code || "27";

  const [selectedRole, setSelectedRole] = useState<PartyType>(partyType || "customer");
  const [entityType, setEntityType] = useState<EntityType>("business");
  const [name, setName] = useState(initialName);
  const [displayName, setDisplayName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [gstRegistered, setGstRegistered] = useState(true);
  const [gstin, setGstin] = useState("");
  const [pan, setPan] = useState("");
  const [businessConstitution, setBusinessConstitution] = useState("Sole Proprietorship");

  const [state, setState] = useState(defaultState);
  const [stateCode, setStateCode] = useState(defaultStateCode);
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [billingAddress, setBillingAddress] = useState("");

  // More Details Collapsible
  const [showMore, setShowMore] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingState, setShippingState] = useState(defaultState);
  const [shippingPincode, setShippingPincode] = useState("");
  const [creditLimit, setCreditLimit] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("Due on Receipt");
  const [preferredPaymentMethod, setPreferredPaymentMethod] = useState("Bank Transfer");
  const [openingBalance, setOpeningBalance] = useState("");
  const [balanceType, setBalanceType] = useState<"Dr" | "Cr">(partyType === "supplier" ? "Cr" : "Dr");
  const [notes, setNotes] = useState("");

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stateMismatchWarning, setStateMismatchWarning] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  // Sync initialName & partyType when modal opens
  useEffect(() => {
    if (open) {
      setName(initialName || "");
      setDisplayName(initialName || "");
      setSelectedRole(partyType || "customer");
      setBalanceType(partyType === "supplier" ? "Cr" : "Dr");
      setEntityType("business");
      setGstRegistered(true);
      setGstin("");
      setPan("");
      setErrors({});
      setStateMismatchWarning(null);

      const st = activeCompany?.state || "Maharashtra";
      setState(st);
      const stObj = getStateByName(st);
      setStateCode(stObj?.code || "27");

      setTimeout(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      }, 100);
    }
  }, [open, initialName, partyType, activeCompany?.state]);

  // Handle Customer Entity Type Toggle (Business vs Individual)
  const handleEntityTypeChange = (type: EntityType) => {
    setEntityType(type);
    if (type === "individual") {
      setGstRegistered(false);
      setBusinessConstitution("Individual / Freelancer");
    } else {
      setGstRegistered(true);
      setBusinessConstitution("Sole Proprietorship");
    }
  };

  // Sync stateCode when state changes
  const handleStateChange = (stateName: string) => {
    setState(stateName);
    const st = getStateByName(stateName);
    const code = st?.code || "";
    setStateCode(code);

    // Check if GSTIN exists and conflicts with selected state
    if (gstin && gstin.length >= 2) {
      const gstinPrefix = gstin.slice(0, 2);
      if (code && gstinPrefix !== code) {
        setStateMismatchWarning(
          `GSTIN starts with state code "${gstinPrefix}", but selected state is "${stateName}" (${code}).`
        );
      } else {
        setStateMismatchWarning(null);
      }
    }
  };

  // Sync PAN, State & State Code when GSTIN changes
  const handleGSTINChange = (val: string) => {
    const clean = val.toUpperCase().trim();
    setGstin(clean);

    if (errors.gstin) {
      setErrors((prev) => ({ ...prev, gstin: "" }));
    }

    if (clean.length >= 2) {
      const code = clean.slice(0, 2);
      const st = getStateByCode(code);
      if (st) {
        setState(st.name);
        setStateCode(st.code);
        setStateMismatchWarning(null);
      } else {
        setStateMismatchWarning(`State code "${code}" is not recognized.`);
      }
    } else {
      setStateMismatchWarning(null);
    }

    // Auto extract PAN (chars 3 to 12)
    if (clean.length >= 12) {
      const extractedPan = clean.slice(2, 12);
      setPan(extractedPan);
    }
  };

  // Validation
  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    if (!name.trim()) {
      errs.name = "Party / Customer name is required";
    }

    if (phone.trim() && phone.replace(/\D/g, "").length !== 10) {
      errs.phone = "Enter a valid 10-digit mobile number";
    }

    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.email = "Enter a valid email address";
    }

    if (gstRegistered) {
      if (gstin.trim()) {
        if (!validateGSTIN(gstin.trim())) {
          errs.gstin = "GSTIN must be 15 alphanumeric characters (e.g. 27AAAAA0000A1Z5)";
        } else {
          // Check state code consistency
          const gstinCode = gstin.trim().slice(0, 2);
          if (stateCode && gstinCode !== stateCode) {
            errs.gstin = `GSTIN prefix (${gstinCode}) does not match selected state (${state} - ${stateCode})`;
          }
        }
      }
    }

    if (pan.trim() && !validatePAN(pan.trim())) {
      errs.pan = "PAN must be 10 characters (e.g. ABCDE1234F)";
    }

    if (pincode.trim() && pincode.replace(/\D/g, "").length !== 6) {
      errs.pincode = "PIN code must be 6 digits";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!validate()) return;

    setSaving(true);
    try {
      const payload: CreatePartyInput = {
        name: name.trim(),
        displayName: displayName.trim() || name.trim(),
        partyType: selectedRole,
        entityType,
        contactPerson: contactPerson.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim().toLowerCase() || undefined,
        gstRegistered,
        gstin: gstRegistered && gstin.trim() ? gstin.trim().toUpperCase() : undefined,
        pan: pan.trim() ? pan.trim().toUpperCase() : (gstin.length >= 12 ? gstin.slice(2, 12).toUpperCase() : undefined),
        businessConstitution,
        billingAddress: billingAddress.trim() || undefined,
        billingCity: city.trim() || undefined,
        billingState: state,
        billingStateCode: stateCode,
        billingPincode: pincode.trim() || undefined,
        billingCountry: "India",
        sameAsBilling,
        shippingAddress: sameAsBilling ? billingAddress.trim() || undefined : shippingAddress.trim() || undefined,
        shippingCity: sameAsBilling ? city.trim() || undefined : shippingCity.trim() || undefined,
        shippingState: sameAsBilling ? state : shippingState,
        shippingPincode: sameAsBilling ? pincode.trim() || undefined : shippingPincode.trim() || undefined,
        shippingCountry: "India",
        creditLimit: parseFloat(creditLimit) || 0,
        paymentTerms,
        preferredPaymentMethod,
        openingBalance: parseFloat(openingBalance) || 0,
        balanceType,
        notes: notes.trim() || undefined,
      };

      const result = await createParty(payload);

      toast({
        title: "Party Saved",
        description: `${result.party.displayName || result.party.name} created and auto-selected.`,
      });

      onPartyCreated?.(result.party, result.selectedLedgerId);
      onOpenChange(false);
    } catch (err: any) {
      console.error("[Quick Party Save Error]:", err);
      toast({
        title: "Creation Error",
        description: err.message || "Failed to create party. Please check fields.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const entityTypeLabel = useMemo(() => {
    if (pan.length >= 4) {
      const char = pan.charAt(3).toUpperCase();
      return PAN_ENTITY_TYPES[char];
    }
    return null;
  }, [pan]);

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      size="standard"
      icon={<Plus className="w-4 h-4 text-primary" />}
      title={`Create New ${selectedRole === "both" ? "Party (Customer & Supplier)" : selectedRole === "customer" ? "Customer" : "Supplier"}`}
      description="Quickly add customer details without leaving this invoice."
      footerLeft={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onOpenChange(false)}
          disabled={saving}
          className="text-xs h-9"
        >
          Cancel <span className="text-[10px] text-muted-foreground ml-1">Esc</span>
        </Button>
      }
      footerRight={
        <Button
          type="submit"
          form="quick-party-form"
          size="sm"
          disabled={saving}
          className="text-xs h-9 gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-sm"
        >
          {saving ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Plus className="w-3.5 h-3.5" />
              <span>Save & Select {selectedRole === "both" ? "Party" : selectedRole === "customer" ? "Customer" : "Supplier"}</span>
            </>
          )}
        </Button>
      }
    >
      <form id="quick-party-form" onSubmit={handleSave} className="space-y-4" onKeyDown={(e) => e.stopPropagation()}>
          {/* Top Selection: Party Role & Customer Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Party Role */}
            <div className="space-y-1">
              <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Party Role</Label>
              <div className="grid grid-cols-3 gap-1 p-1 bg-muted/70 rounded-lg border border-border/70 text-xs text-center">
                <button
                  type="button"
                  onClick={() => { setSelectedRole("customer"); setBalanceType("Dr"); }}
                  className={`py-1.5 px-1 rounded-md font-medium transition-all ${selectedRole === "customer" ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Customer
                </button>
                <button
                  type="button"
                  onClick={() => { setSelectedRole("supplier"); setBalanceType("Cr"); }}
                  className={`py-1.5 px-1 rounded-md font-medium transition-all ${selectedRole === "supplier" ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Supplier
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedRole("both")}
                  className={`py-1.5 px-1 rounded-md font-medium transition-all ${selectedRole === "both" ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Both
                </button>
              </div>
            </div>

            {/* Customer Type: Business vs Individual */}
            <div className="space-y-1">
              <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Customer Type</Label>
              <div className="grid grid-cols-2 gap-1 p-1 bg-muted/70 rounded-lg border border-border/70 text-xs text-center">
                <button
                  type="button"
                  onClick={() => handleEntityTypeChange("business")}
                  className={`py-1.5 px-2 rounded-md font-medium transition-all ${entityType === "business" ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Business (B2B)
                </button>
                <button
                  type="button"
                  onClick={() => handleEntityTypeChange("individual")}
                  className={`py-1.5 px-2 rounded-md font-medium transition-all ${entityType === "individual" ? "bg-background text-foreground shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Individual (B2C)
                </button>
              </div>
            </div>
          </div>

          {/* Party Name & Display Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="qp-name" className="text-xs font-semibold uppercase tracking-wider flex items-center justify-between">
                <span>{entityType === "individual" ? "Customer Full Name" : "Business / Legal Name"} *</span>
                {errors.name && <span className="text-[11px] text-destructive font-normal">{errors.name}</span>}
              </Label>
              <div className="relative">
                {entityType === "individual" ? (
                  <User className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                ) : (
                  <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                )}
                <Input
                  id="qp-name"
                  ref={nameInputRef}
                  placeholder={entityType === "individual" ? "e.g. Ramesh Chandra" : "e.g. Apex Enterprises Pvt Ltd"}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!displayName || displayName === name) {
                      setDisplayName(e.target.value);
                    }
                    if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
                  }}
                  className={`pl-8 h-9 text-sm ${errors.name ? "border-destructive focus-visible:ring-destructive" : ""}`}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="qp-display-name" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Display Name / Trade Name
              </Label>
              <Input
                id="qp-display-name"
                placeholder="Name shown on invoices & dropdowns"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>

          {/* Contact Person & Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="qp-phone" className="text-xs font-semibold uppercase tracking-wider flex items-center justify-between">
                <span>Mobile Number</span>
                {errors.phone && <span className="text-[11px] text-destructive font-normal">{errors.phone}</span>}
              </Label>
              <div className="relative">
                <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  id="qp-phone"
                  placeholder="e.g. 9876543210"
                  maxLength={10}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, ""));
                    if (errors.phone) setErrors((prev) => ({ ...prev, phone: "" }));
                  }}
                  className={`pl-8 h-9 text-sm ${errors.phone ? "border-destructive" : ""}`}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="qp-email" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
                <span>Email Address</span>
                {errors.email && <span className="text-[11px] text-destructive font-normal">{errors.email}</span>}
              </Label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  id="qp-email"
                  type="email"
                  placeholder="e.g. accounts@client.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
                  }}
                  className={`pl-8 h-9 text-sm ${errors.email ? "border-destructive" : ""}`}
                />
              </div>
            </div>
          </div>

          {/* Contact Person (for Business) */}
          {entityType === "business" && (
            <div className="space-y-1.5">
              <Label htmlFor="qp-contact" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Contact Person Name
              </Label>
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  id="qp-contact"
                  placeholder="e.g. Rajesh Kumar (Manager)"
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                  className="pl-8 h-9 text-sm"
                />
              </div>
            </div>
          )}

          {/* GST Toggle Card */}
          <div className="p-3 rounded-lg border border-border bg-muted/20 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-foreground">Is this customer registered under GST?</p>
                <p className="text-[11px] text-muted-foreground">
                  {gstRegistered ? "Tax invoice with 15-digit GSTIN & ITC claims" : "Unregistered consumer / B2C invoice (GSTIN not required)"}
                </p>
              </div>
              <Switch checked={gstRegistered} onCheckedChange={setGstRegistered} />
            </div>

            {gstRegistered ? (
              <div className="space-y-2 pt-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="qp-gstin" className="text-[11px] font-semibold uppercase tracking-wider flex items-center justify-between">
                      <span>GSTIN (15 Digits)</span>
                      {validateGSTIN(gstin) && (
                        <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-0.5">
                          <CheckCircle2 className="w-3 h-3" /> Valid GSTIN
                        </span>
                      )}
                    </Label>
                    <Input
                      id="qp-gstin"
                      placeholder="e.g. 27AAAAA0000A1Z5"
                      maxLength={15}
                      value={gstin}
                      onChange={(e) => handleGSTINChange(e.target.value)}
                      className={`font-mono uppercase text-xs h-9 tracking-wider ${errors.gstin ? "border-destructive" : ""}`}
                    />
                    {errors.gstin && <p className="text-[10px] text-destructive mt-0.5">{errors.gstin}</p>}
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="qp-pan" className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
                      <span>PAN (10 Digits)</span>
                      {entityTypeLabel && (
                        <Badge variant="outline" className="text-[9px] py-0 px-1 font-normal bg-primary/5 text-primary">
                          {entityTypeLabel}
                        </Badge>
                      )}
                    </Label>
                    <Input
                      id="qp-pan"
                      placeholder="AAAAA0000A"
                      maxLength={10}
                      value={pan}
                      onChange={(e) => setPan(e.target.value.toUpperCase().trim())}
                      className="font-mono uppercase text-xs h-9 tracking-wider"
                    />
                    {errors.pan && <p className="text-[10px] text-destructive mt-0.5">{errors.pan}</p>}
                  </div>
                </div>

                {stateMismatchWarning && (
                  <div className="flex items-center gap-1.5 p-2 rounded bg-amber-500/10 border border-amber-500/20 text-amber-700 text-[11px]">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span>{stateMismatchWarning}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 pt-0.5">
                <ShieldCheck className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span>Marked as Unregistered. Supply will be processed as consumer / B2C invoice.</span>
              </div>
            )}
          </div>

          {/* State, City & PIN Code */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5 sm:col-span-1">
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                State & GST Code *
              </Label>
              <Select value={state} onValueChange={handleStateChange}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  {INDIAN_STATES_WITH_GST_CODES.map((s) => (
                    <SelectItem key={s.code} value={s.name} className="text-xs">
                      <span>{s.name} ({s.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="qp-city" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                City
              </Label>
              <Input
                id="qp-city"
                placeholder="e.g. Pune"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="h-9 text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="qp-pin" className="text-xs font-semibold uppercase tracking-wider flex items-center justify-between">
                <span>PIN Code</span>
                {errors.pincode && <span className="text-[10px] text-destructive">{errors.pincode}</span>}
              </Label>
              <Input
                id="qp-pin"
                placeholder="e.g. 411001"
                maxLength={6}
                value={pincode}
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                className="h-9 text-xs"
              />
            </div>
          </div>

          {/* Billing Street Address */}
          <div className="space-y-1.5">
            <Label htmlFor="qp-addr" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Billing Address
            </Label>
            <Input
              id="qp-addr"
              placeholder="e.g. Plot No 12, Bhosari MIDC, Industrial Area"
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          {/* Collapsible: More Accounting & Shipping Details */}
          <Collapsible open={showMore} onOpenChange={setShowMore} className="border border-border/80 rounded-lg p-3 bg-card/60">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center justify-between w-full text-xs font-semibold text-foreground hover:text-primary transition-colors cursor-pointer"
              >
                <span>More Details (Opening Balance, Credit Terms, Shipping Address)</span>
                {showMore ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-3">
              {/* Opening Balance & Balance Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="qp-op-bal" className="text-[11px] text-muted-foreground">Opening Balance (₹)</Label>
                  <Input
                    id="qp-op-bal"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={openingBalance}
                    onChange={(e) => setOpeningBalance(e.target.value)}
                    className="h-8 text-xs font-mono"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Balance Type</Label>
                  <Select value={balanceType} onValueChange={(v: "Dr" | "Cr") => setBalanceType(v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Dr">Debit (Receivable - Party owes you)</SelectItem>
                      <SelectItem value="Cr">Credit (Payable / Advance received)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Credit Limit & Terms */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Payment Terms</Label>
                  <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                      <SelectItem value="Net 15">Net 15 Days</SelectItem>
                      <SelectItem value="Net 30">Net 30 Days</SelectItem>
                      <SelectItem value="Net 45">Net 45 Days</SelectItem>
                      <SelectItem value="Net 60">Net 60 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="qp-credit" className="text-[11px] text-muted-foreground">Credit Limit (₹)</Label>
                  <Input
                    id="qp-credit"
                    type="number"
                    placeholder="0.00 (No limit if 0)"
                    value={creditLimit}
                    onChange={(e) => setCreditLimit(e.target.value)}
                    className="h-8 text-xs font-mono"
                  />
                </div>
              </div>

              {/* Shipping Address Toggle */}
              <div className="flex items-center space-x-2 pt-1">
                <Checkbox
                  id="qp-same-addr"
                  checked={sameAsBilling}
                  onCheckedChange={(checked) => setSameAsBilling(Boolean(checked))}
                />
                <label
                  htmlFor="qp-same-addr"
                  className="text-xs text-muted-foreground cursor-pointer select-none"
                >
                  Shipping address is identical to billing address
                </label>
              </div>

              {!sameAsBilling && (
                <div className="p-2.5 rounded-lg border border-border bg-muted/20 space-y-2">
                  <Label className="text-[11px] font-semibold text-foreground">Separate Shipping Address</Label>
                  <Input
                    placeholder="Shipping street address"
                    value={shippingAddress}
                    onChange={(e) => setShippingAddress(e.target.value)}
                    className="h-8 text-xs"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Shipping city"
                      value={shippingCity}
                      onChange={(e) => setShippingCity(e.target.value)}
                      className="h-8 text-xs"
                    />
                    <Input
                      placeholder="Shipping PIN"
                      maxLength={6}
                      value={shippingPincode}
                      onChange={(e) => setShippingPincode(e.target.value.replace(/\D/g, ""))}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-1">
                <Label htmlFor="qp-notes" className="text-[11px] text-muted-foreground">Internal Notes / Delivery Remarks</Label>
                <Textarea
                  id="qp-notes"
                  placeholder="Optional delivery instructions or customer notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="text-xs min-h-[50px]"
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

      </form>
    </FormModal>
  );
}
